/**
 * dateLocal.js — Iter 158 (Josh) — Safe local-date helpers.
 *
 * Problem: `new Date("2026-06-04")` parses as UTC midnight, so reps west of
 * UTC see it render as 6/3/2026 in `toLocaleDateString()`. This was the
 * root cause of issue reports #10, #34, #45 ("task created today shows
 * yesterday as due date"). Always parse YYYY-MM-DD strings as LOCAL.
 */

/** Parse a `YYYY-MM-DD` (or ISO with time) string as a local-date. */
export const parseLocalDate = (s) => {
  if (!s) return null;
  if (s instanceof Date) return s;
  if (typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  // ISO datetime — let the engine handle it (timezone is encoded).
  return new Date(s);
};

/** Format a date-ish value as a local short date (e.g. "6/4/2026"). */
export const fmtLocalDate = (s, fallback = "—") => {
  const d = parseLocalDate(s);
  if (!d || isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString();
};

/** True if the value represents "today" in the local timezone. */
export const isToday = (s) => {
  const d = parseLocalDate(s);
  if (!d) return false;
  return d.toDateString() === new Date().toDateString();
};

/** True if the value represents "tomorrow" in the local timezone. */
export const isTomorrow = (s) => {
  const d = parseLocalDate(s);
  if (!d) return false;
  const tom = new Date();
  tom.setDate(tom.getDate() + 1);
  return d.toDateString() === tom.toDateString();
};

/** True if the date is in the past relative to "now" (local). */
export const isPast = (s) => {
  const d = parseLocalDate(s);
  if (!d) return false;
  return d.getTime() < Date.now();
};
